import { AA_NORMAL_TEXT, accentInkVars, contrastRatio, mixHex, onAccentInk } from "@njbsoft/store";

/** The brand a utility with no letter branding prints, and the brand the
 *  portal falls back to for a value that is not a `#RRGGBB` hex. */
export const DEFAULT_BRAND_COLOR = "#0F4C81";

/** The dark theme's surface, the background every dark-mode brand role is
 *  measured and mixed against. */
export const DARK_SURFACE = "#171D25";

/** The custom properties `applyBrand` publishes on `<html>`. */
export type BrandVar =
  | "--color-brand"
  | "--color-brand-ink"
  | "--color-brand-tint"
  | "--color-brand-dark"
  | "--color-brand-fg"
  | "--color-brand-tile"
  | "--color-accent-ink-dark";

const BRAND_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

/** The brand's share of the tint the letters fill their action box with. */
const BRAND_TINT_RATIO = 0.08;

/** The brand's share of a glyph tile on the dark surface. */
const DARK_TILE_RATIO = 0.14;

/** How many equal steps `brandOnDark` takes from the brand to white. */
const ON_DARK_STEPS = 20;

export function isBrandColor(value: unknown): value is string {
  return typeof value === "string" && BRAND_COLOR_RE.test(value);
}

/** The letters' 8% tint, channel by channel toward white with rounding: the
 *  same arithmetic as the API's `noticetemplate.BrandTint`. */
export function brandTint(brand: string): string {
  return mixHex(brand, "#ffffff", BRAND_TINT_RATIO);
}

/**
 * The brand made legible on a dark surface: the first of 21 candidates, from
 * the brand itself (step 0) to white (step 20) in equal steps toward white,
 * that reaches the WCAG AA normal-text floor against `surface`. A brand dark
 * enough to vanish on the dark theme is lightened only as far as legibility
 * needs, so it keeps as much of its hue as it can.
 */
export function brandOnDark(brand: string, surface: string = DARK_SURFACE): string {
  for (let k = 0; k <= ON_DARK_STEPS; k += 1) {
    const candidate = mixHex(brand, "#ffffff", (ON_DARK_STEPS - k) / ON_DARK_STEPS);
    if (contrastRatio(candidate, surface) >= AA_NORMAL_TEXT) return candidate;
  }
  return "#ffffff";
}

/**
 * Every brand role for one theme. CSS cannot measure contrast, so the roles
 * that differ by theme are resolved here: the foreground (links, the active nav
 * item, focus rings) is the brand on light and `brandOnDark` on dark, and the
 * glyph tile is the tint on light and a 14% mix into the dark surface on dark.
 * A value that is not a `#RRGGBB` hex is replaced by the default first.
 */
export function brandVars(
  brand: string,
  resolvedTheme: "light" | "dark",
): Record<BrandVar, string> {
  const b = isBrandColor(brand) ? brand : DEFAULT_BRAND_COLOR;
  const tint = brandTint(b);
  const onDark = brandOnDark(b);
  return {
    "--color-brand": b,
    "--color-brand-ink": onAccentInk(b),
    "--color-brand-tint": tint,
    "--color-brand-dark": onDark,
    "--color-brand-fg": resolvedTheme === "dark" ? onDark : b,
    "--color-brand-tile":
      resolvedTheme === "dark" ? mixHex(b, DARK_SURFACE, DARK_TILE_RATIO) : tint,
    "--color-accent-ink-dark": accentInkVars(b).dark,
  };
}

/** Publishes `brandVars(brand, resolvedTheme)` on `<html>`, and nothing else. */
export function applyBrand(brand: string, resolvedTheme: "light" | "dark"): void {
  const style = document.documentElement.style;
  for (const [name, value] of Object.entries(brandVars(brand, resolvedTheme))) {
    style.setProperty(name, value);
  }
}
